<script setup lang="ts">
import { nextTick, ref, shallowRef, watch } from 'vue'
import type { PaginationQueryParams } from '@/@vxapp/@Enums/PaginationField'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import { useRuleStore } from '@/@vxapp/Rule/useRuleStore'
import RuleDialog from '@/components/Dialogs/Rules/RuleDialog.vue'
import RulesDatatable from '@/components/Tables/Rules/RulesDatatable.vue'
import { DialogDisplayType } from '@/enums/dialog-display-type'
import { useAppNotifications } from '@/plugins/toast/composables/useAppNotifications'

type RuleData = Record<string, any>
definePage({ meta: { ...PageMeta.Rules.List.Meta, pageTitle: PageMeta.Rules.List.Title } })

const log = createLogger('RulesListPage')
const notify = useAppNotifications()
const route = useRoute()
const router = useRouter()
const store = useRuleStore() as any
const terminal = ref<InstanceType<typeof RulesDatatable> | null>(null)
const selectedRule = ref<RuleData | null>(null)
const isRuleDialogVisible = shallowRef(false)
const isDeleteDialogVisible = shallowRef(false)
const displayType = ref<number>(DialogDisplayType.CREATE)
const searchQuery = ref('')
const resultCount = ref(0)
const defaults: PaginationQueryParams = { ...PaginationField.QueryParams, sort: '-id' }
const limitItems = [10, 25, 50, 100]

const sortItems = [
  { title: 'Newest blocks', value: '-id' },
  { title: 'Oldest blocks', value: '+id' },
]

function scalar(value: unknown) {
  const normalized = Array.isArray(value) ? value[0] : value

  return typeof normalized === 'string' || typeof normalized === 'number'
    ? String(normalized)
    : undefined
}
function positiveInteger(value: unknown, fallback: number) {
  const parsed = Number.parseInt(scalar(value) ?? '', 10)

  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}
function normalize(source: Record<string, unknown>): PaginationQueryParams {
  const limit = positiveInteger(source.limit, defaults.limit)
  const search = scalar(source.search)?.trim()

  return {
    ...defaults,
    page: positiveInteger(source.page, defaults.page),
    limit: limitItems.includes(limit) ? limit : defaults.limit,
    sort: scalar(source.sort) === '+id' ? '+id' : '-id',
    search: search || undefined,
  }
}
function same(left: PaginationQueryParams, right: PaginationQueryParams) {
  return (
    left.page === right.page &&
    left.limit === right.limit &&
    left.sort === right.sort &&
    left.search === right.search
  )
}
function applyRoute() {
  const incoming = normalize(route.query as Record<string, unknown>)
  const current = normalize(store.queryParams ?? defaults)

  searchQuery.value = incoming.search ?? ''
  if (same(current, incoming)) return false
  store.queryParams = incoming

  return true
}
function routeQuery(source: Record<string, unknown>) {
  const query = normalize(source)

  return {
    ...(query.page !== defaults.page ? { page: String(query.page) } : {}),
    ...(query.limit !== defaults.limit ? { limit: String(query.limit) } : {}),
    ...(query.sort !== defaults.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
  }
}
async function fetchRules() {
  await terminal.value?.fetchRules()
}
async function updateQuery(patch: Partial<PaginationQueryParams>) {
  const next = normalize({ ...store.queryParams, ...patch })

  store.queryParams = next

  const query = routeQuery({ ...next })

  if (JSON.stringify(route.query) !== JSON.stringify(query)) await router.replace({ query })
  await nextTick()
  await fetchRules()
}
async function handleSearch() {
  await updateQuery({ search: searchQuery.value.trim() || undefined, page: 1 })
}
async function handleClear() {
  searchQuery.value = ''
  await updateQuery({ search: undefined, page: 1 })
}
async function handleSort(value: unknown) {
  await updateQuery({ sort: String(value), page: 1 })
}
async function handleLimit(value: unknown) {
  await updateQuery({ limit: Number(value), page: 1 })
}
async function handlePage(page: number) {
  await updateQuery({ page })
}
function handleLoaded(count: number) {
  resultCount.value = count
}
async function handleCreate() {
  const { template, responseCode } = await store.fetchTemplate({ withDefaults: true })

  if (responseCode !== ResponseCode.OK) {
    notify.error('The rule editor could not be loaded')

    return
  }
  selectedRule.value = cloneReactiveData(template ?? {})
  displayType.value = DialogDisplayType.CREATE
  isRuleDialogVisible.value = true
}
async function loadRule(guid: string) {
  store.forceReload = true

  const { rule, responseCode } = await store.fetch(guid)

  store.forceReload = false
  if (responseCode !== ResponseCode.OK || !rule) {
    log.error('Failed to load rule', { guid, responseCode })
    notify.error('The rule could not be loaded')

    return false
  }
  selectedRule.value = cloneReactiveData(rule)

  return true
}
async function handleEdit(guid: string) {
  if (await loadRule(guid)) {
    displayType.value = DialogDisplayType.EDIT
    isRuleDialogVisible.value = true
  }
}
async function handleView(guid: string) {
  if (await loadRule(guid)) {
    displayType.value = DialogDisplayType.VIEW
    isRuleDialogVisible.value = true
  }
}
async function handleDelete(guid: string) {
  if (await loadRule(guid)) isDeleteDialogVisible.value = true
}
async function handleCreateSave(payload: RuleData) {
  try {
    const result = await store.create(payload, false)

    if ([ResponseCode.OK, ResponseCode.CREATED].includes(result.responseCode)) {
      notify.success(result.message ?? 'Rule created')
      await fetchRules()
    } else notify.error(result.message ?? 'Rule could not be created')
  } catch (error: any) {
    notify.error(error?.message ?? 'Rule could not be created')
  }
}
async function handleUpdateSave(payload: RuleData) {
  try {
    const result = await store.update(payload, false)

    if (result.responseCode === ResponseCode.OK) {
      notify.success(result.message ?? 'Rule updated')
      await fetchRules()
    } else notify.error(result.message ?? 'Rule could not be updated')
  } catch (error: any) {
    notify.error(error?.message ?? 'Rule could not be updated')
  }
}
async function confirmDelete() {
  const guid = selectedRule.value?.ruleGUID

  if (!guid) return
  const result = await store.delete(guid)

  if (result.responseCode === ResponseCode.OK) {
    notify.success('Rule deleted')
    isDeleteDialogVisible.value = false
    await fetchRules()
  } else notify.error(result.error ?? 'Rule could not be deleted')
}
function closeDelete() {
  isDeleteDialogVisible.value = false
}

store.queryParams = { ...defaults }
applyRoute()
onMounted(fetchRules)
watch(
  () => route.query,
  async () => {
    if (!applyRoute()) return
    await nextTick()
    await fetchRules()
  },
  { deep: true }
)
</script>

<template>
  <main
    class="rules-workspace"
    data-testid="rules-workspace"
  >
    <section class="rules-workspace__hero">
      <span>AUTOMATION / EXECUTION FLOW</span>
      <h1>Turn operating logic into calm, inspectable blocks.</h1>
      <p>
        Build conditional rules, preview their action pipelines and open the full editor when the
        logic needs to change.
      </p>
      <VBtn
        color="primary"
        @click="handleCreate"
        >Create rule block</VBtn
      >
      <aside>
        <code>ACTIVE VIEW</code><strong>{{ resultCount }}</strong
        ><span>rule blocks</span>
      </aside>
    </section>
    <VCard class="rules-workspace__toolbar"
      ><VCardText>
        <VForm
          class="rules-workspace__search"
          role="search"
          @submit.prevent="handleSearch"
        >
          <!-- vortex-validation-ignore-element: server search is non-validation filter UI -->
          <AppTextField
            v-model="searchQuery"
            aria-label="Search rules"
            clearable
            hide-details
            placeholder="Search rule blocks"
            vortex-validation-ignore-element
            @click:clear="handleClear"
          /><VBtn
            color="primary"
            type="button"
            @click="handleSearch"
            >Search</VBtn
          >
        </VForm>
        <!-- vortex-validation-ignore-element: result sorting is non-validation UI -->
        <AppSelect
          :model-value="store.queryParams?.sort"
          :items="sortItems"
          aria-label="Sort rules"
          hide-details
          label="Sort"
          vortex-validation-ignore-element
          @update:model-value="handleSort"
        />
        <!-- vortex-validation-ignore-element: page size is non-validation UI -->
        <AppSelect
          :model-value="store.queryParams?.limit"
          :items="limitItems"
          aria-label="Rules per page"
          hide-details
          label="Per page"
          vortex-validation-ignore-element
          @update:model-value="handleLimit"
        /> </VCardText
    ></VCard>
    <RulesDatatable
      ref="terminal"
      :query-params="store.queryParams"
      @delete="handleDelete"
      @edit="handleEdit"
      @loaded="handleLoaded"
      @page-change="handlePage"
      @view="handleView"
    />
  </main>
  <RuleDialog
    v-if="selectedRule"
    v-model:is-dialog-visible="isRuleDialogVisible"
    :display-type="displayType"
    :rule-data="selectedRule"
    @create="handleCreateSave"
    @update="handleUpdateSave"
  />
  <VDialog
    v-model="isDeleteDialogVisible"
    max-width="420"
    ><VCard v-if="selectedRule">
      <VCardTitle>Delete rule block?</VCardTitle
      ><VCardText>This removes “{{ selectedRule.ruleName }}” from the execution flow.</VCardText>
      <VCardActions
        ><VBtn @click="closeDelete">Cancel</VBtn
        ><VBtn
          color="error"
          @click="confirmDelete"
          >Delete</VBtn
        ></VCardActions
      >
    </VCard></VDialog
  >
</template>

<style scoped lang="scss">
.rules-workspace {
  display: grid;
  min-inline-size: 0;
  gap: 1rem;
  background: rgb(var(--v-theme-background));
  color: rgba(var(--v-theme-on-background), var(--v-high-emphasis-opacity));
  font-family: Geist, Inter, system-ui, sans-serif;
}
.rules-workspace > * {
  box-sizing: border-box;
  inline-size: 100%;
  max-inline-size: 100%;
  min-inline-size: 0;
}
.rules-workspace__hero {
  position: relative;
  display: grid;
  min-block-size: 30rem;
  align-content: center;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 2.5rem;
  background: rgb(var(--v-theme-surface));
  padding: clamp(2rem, 8vw, 6rem);
}
.rules-workspace__hero > span {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 0.72rem;
  letter-spacing: 0.15rem;
}
.rules-workspace__hero h1 {
  max-inline-size: 14ch;
  margin: 1rem 0;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: clamp(3rem, 7vw, 5rem);
  font-weight: 400;
  letter-spacing: -2.4px;
  line-height: 1;
}
.rules-workspace__hero > p {
  max-inline-size: 58ch;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 1.1rem;
  line-height: 1.5;
}
.rules-workspace__hero :deep(.v-btn) {
  justify-self: start;
  min-block-size: 3rem;
  margin-block-start: 1rem;
  border-radius: 50px;
}
.rules-workspace__hero aside {
  position: absolute;
  inset: auto clamp(2rem, 6vw, 5rem) clamp(2rem, 6vw, 5rem) auto;
  display: grid;
  min-inline-size: 12rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 0.75rem;
  background: rgba(var(--v-theme-on-surface), 0.04);
  gap: 0.25rem;
  padding: 1.5rem;
}
.rules-workspace__hero aside strong {
  font-size: 3rem;
  font-weight: 400;
}
.rules-workspace__hero aside code,
.rules-workspace__hero aside span {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}
.rules-workspace__toolbar {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 0.875rem;
  background: rgb(var(--v-theme-surface));
  box-shadow: none;
}
.rules-workspace__toolbar :deep(.v-card-text) {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: minmax(16rem, 1fr) minmax(10rem, 0.3fr) minmax(8rem, 0.2fr);
  padding: 0.75rem;
}
.rules-workspace__search {
  display: grid;
  min-inline-size: 0;
  gap: 0.5rem;
  grid-template-columns: minmax(0, 1fr) auto;
}
.rules-workspace__search :deep(.v-btn) {
  min-block-size: 2.75rem;
  border-radius: 50px;
}
@media (max-width: 799px) {
  .rules-workspace__toolbar :deep(.v-card-text) {
    grid-template-columns: 1fr;
  }
  .rules-workspace__hero aside {
    position: relative;
    inset: auto;
    margin-block-start: 2rem;
  }
}
@media (max-width: 425px) {
  .rules-workspace__hero {
    min-block-size: auto;
    border-radius: 0.875rem;
    padding: 1.5rem;
  }
  .rules-workspace__hero h1 {
    overflow-wrap: anywhere;
    font-size: clamp(2.25rem, 11vw, 3rem);
  }
  .rules-workspace__search {
    grid-template-columns: 1fr;
  }
}
</style>
